/**
 * Quem já levantou a taça: as edições encerradas, com campeão, final e prêmio.
 *
 * O campeão mais recente ganha o palco (escudo grande, troféu, placar da
 * final); as edições anteriores formam a linha do tempo ao lado. Tudo vem de
 * `/api/tournaments?status=finished` -- a mesma fonte das páginas de edição.
 */

import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { queries, type TournamentSummary } from '@/lib/api';
import { money } from '@/lib/format';
import { TeamCrest } from '@/components/ui';
import { TrophyMark } from '@/components/TrophyMark';
import { gsap, ScrollTrigger, useGSAP } from '@/lib/motion';

const dataCurta = (s: number | null) =>
  s ? new Date(s * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

function Placar({ t }: { t: TournamentSummary }) {
  const f = t.finalScore;
  if (!f) return null;
  const penaltis = f.championPenalties != null && f.runnerUpPenalties != null;
  return (
    <span className="tabular">
      {f.champion} × {f.runnerUp}
      {penaltis && <span className="text-faint"> ({f.championPenalties} × {f.runnerUpPenalties} pên.)</span>}
    </span>
  );
}

export function GaleriaDeCampeoes() {
  const { data } = useQuery(queries.tournaments('finished'));
  const raiz = useRef<HTMLElement>(null);
  const edicoes = (data?.tournaments ?? [])
    .filter((t) => t.champion)
    .sort((a, b) => (b.startsAt ?? 0) - (a.startsAt ?? 0));

  useGSAP(
    () => {
      if (!raiz.current || edicoes.length === 0) return;
      const tl = gsap.timeline({ scrollTrigger: { trigger: raiz.current, start: 'top 70%', once: true } });
      tl.from('[data-palco-trofeu]', { y: 60, autoAlpha: 0, rotate: -8, duration: 1.1 })
        .from('[data-palco-texto]', { y: 30, autoAlpha: 0, stagger: 0.08, duration: 0.9 }, '<0.15')
        .from('[data-edicao]', { x: 40, autoAlpha: 0, stagger: 0.07, duration: 0.8 }, '<0.2');
      return () => tl.scrollTrigger?.kill();
    },
    { dependencies: [edicoes.length], scope: raiz },
  );

  const [atual, ...anteriores] = edicoes;
  if (!atual) return null;
  const campeao = atual.champion!;

  return (
    <section ref={raiz} className="relative z-10 overflow-hidden border-t border-line py-24" aria-labelledby="campeoes-titulo">
      <div className="mx-auto max-w-ucc px-6">
        <h2 id="campeoes-titulo" className="ucc-display mb-12 text-[clamp(2.5rem,6vw,4.5rem)] text-paper">
          Quem já levantou <span className="text-gold-ink">a taça</span>
        </h2>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
          {/* Palco: o campeão mais recente */}
          <Link
            to={`/campeonatos/${atual.slug}`}
            className="group relative flex min-h-[24rem] min-w-0 flex-col justify-end overflow-hidden rounded-2xl border border-gold/40 bg-[linear-gradient(160deg,rgb(248_212_40/0.14),var(--color-surface)_45%,var(--color-void))] p-7 sm:p-10"
          >
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(30rem_20rem_at_78%_30%,rgb(248_212_40/0.22),transparent_70%)]" />
            <TrophyMark
              data-palco-trofeu
              className="pointer-events-none absolute top-6 right-4 h-36 w-auto drop-shadow-[0_24px_40px_rgb(0_0_0/0.6)] transition-transform duration-[--duration-hero] ease-[--ease-signature] group-hover:-translate-y-2 group-hover:rotate-3 sm:right-12 sm:h-72"
            />
            <div className="relative flex flex-col gap-4">
              <p data-palco-texto className="text-sm font-semibold tracking-wider text-gold-ink uppercase">
                {['Campeão', atual.edition, dataCurta(atual.startsAt)].filter(Boolean).join(' · ')}
              </p>
              <div data-palco-texto className="flex items-center gap-4">
                <TeamCrest team={campeao} size="xl" className="ring-2 ring-gold/60" />
                <p className="ucc-display min-w-0 text-[clamp(2.2rem,6vw,4.5rem)] break-words text-paper">{campeao.name}</p>
              </div>
              <div data-palco-texto className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-line-strong pt-4 text-sm text-muted">
                {atual.runnerUp && (
                  <span>
                    Final: <b className="text-paper"><Placar t={atual} /></b> contra {atual.runnerUp.name}
                  </span>
                )}
                {atual.prizePoolCents > 0 && (
                  <span>
                    Premiação <b className="ucc-display tabular text-2xl text-gold-ink">{money(atual.prizePoolCents)}</b>
                  </span>
                )}
              </div>
            </div>
          </Link>

          {/* Linha do tempo das edições anteriores */}
          {anteriores.length > 0 && (
            <ol className="flex min-w-0 flex-col divide-y divide-line rounded-2xl border border-line bg-surface/40">
              {anteriores.map((t) => (
                <li key={t.id} data-edicao>
                  <Link
                    to={`/campeonatos/${t.slug}`}
                    className="group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-surface-2/70"
                  >
                    <TeamCrest team={t.champion} size="lg" />
                    <div className="min-w-0 flex-1">
                      <p className="ucc-display truncate text-2xl text-paper transition-colors group-hover:text-gold-ink">{t.champion!.name}</p>
                      <p className="truncate text-xs text-faint">
                        {dataCurta(t.startsAt)}
                        {t.runnerUp && (
                          <>
                            {t.startsAt ? ' · ' : ''}
                            <Placar t={t} /> contra {t.runnerUp.name}
                          </>
                        )}
                      </p>
                    </div>
                    {t.prizePoolCents > 0 && (
                      <p className="ucc-display tabular shrink-0 text-2xl text-gold-ink">{money(t.prizePoolCents)}</p>
                    )}
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}
